import { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import {
  AdminPanelSettings,
  Assignment,
  BarChart,
  Campaign,
  CalendarMonth,
  ChevronRight,
  DashboardCustomize,
  Email,
  ExpandMore,
  FactCheck,
  Groups,
  HomeWork,
  Key,
  LibraryBooks,
  ManageAccounts,
  MenuBook,
  Money,
  Paid,
  Percent,
  Person,
  PriceChange,
  ReceiptLong,
  School,
  Settings,
  Sms,
  TaskAlt,
  Tune,
  VerifiedUser,
  Work,
} from '@mui/icons-material';

import Restricted from '../../PermissionProvider/Restricted';

function SectionButton({
  title,
  icon,
  open,
  onClick,
  level = 0,
  color = 'primary',
}) {
  const Icon = icon;

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        pl: 1.5 + level * 1.5,
        bgcolor: open ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: `${color}.main` }}>
        <Icon fontSize='small' />
      </ListItemIcon>

      <ListItemText
        primary={title}
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: 700,
        }}
      />

      {open ? (
        <ExpandMore fontSize='small' />
      ) : (
        <ChevronRight fontSize='small' />
      )}
    </ListItemButton>
  );
}

function MenuLink({ to, icon, label, level = 1 }) {
  const location = useLocation();
  const active = location.pathname === to;
  const Icon = icon;

  return (
    <ListItemButton
      component={Link}
      to={to}
      selected={active}
      sx={{
        borderRadius: 2,
        mb: 0.25,
        pl: 1.5 + level * 2,
        color: 'text.primary',
        textDecoration: 'none',
        '&.Mui-selected': {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '& .MuiListItemIcon-root': {
            color: 'primary.contrastText',
          },
          '&:hover': {
            bgcolor: 'primary.dark',
          },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 34 }}>
        <Icon fontSize='small' />
      </ListItemIcon>

      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: 13,
          fontWeight: active ? 700 : 500,
        }}
      />
    </ListItemButton>
  );
}

const MainMenu = ({ params }) => {
  const [displayAuth, toggleAuthentication] = useState(false);
  const [displayScriptantia, setDisplayScriptantia] = useState(true);
  const [displayPalanta, togglePalanta] = useState(true);

  const [displayScriptantiaMasterDate, toggleScriptantiaMasterDate] = useState(
    params.parameters.TORZSADATOK_COLLAPSED === '0',
  );

  const [displayScriptantiaMovements, toggleScriptantiaMovements] = useState(
    params.parameters.INVOICE_MENU_COLLAPSED === '0',
  );

  const [displayScriptantiaEducation, toggleScriptantiaEducation] = useState(
    params.parameters.OKTATAS_COLLAPSED === '0',
  );

  const [displayScriptantiaPayments, toggleScriptantiaPayments] = useState(
    params.parameters.PAYMENTS_COLLAPSED === '0',
  );

  const [displayScriptantiaTasks, toggleScriptantiaTasks] = useState(
    params.parameters.TASKS_COLLAPSED === '0',
  );

  const [displayScriptantiaReports, toggleScriptantiaReports] = useState(
    params.parameters.REPORTS_MENU_COLLAPSED === '0',
  );

  const [displayScriptantiaCampaigns, toggleScriptantiaCampaigns] = useState(
    params.parameters.CAMPAIGNS_MENU_COLLAPSED === '0',
  );

  return (
    <Fragment>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ px: 1, py: 1 }}>
            <Typography variant='subtitle1' fontWeight={800}>
              Oldal adminisztráció
            </Typography>

            <Typography variant='caption' color='text.secondary'>
              Menüpontok és admin felületek
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <List dense disablePadding>
            <Restricted to='scriptantia.menu'>
              <SectionButton
                title='Iskola Adminisztráció'
                icon={School}
                open={displayScriptantia}
                onClick={() => setDisplayScriptantia((prev) => !prev)}
                color='warning'
              />

              <Collapse in={displayScriptantia} timeout='auto' unmountOnExit>
                <Box sx={{ pl: 0.5 }}>
                  <Restricted to='scriptantiaEducation.menu'>
                    <SectionButton
                      title='Oktatás'
                      icon={MenuBook}
                      open={displayScriptantiaEducation}
                      onClick={() =>
                        toggleScriptantiaEducation((prev) => !prev)
                      }
                      level={1}
                      color='info'
                    />

                    <Collapse
                      in={displayScriptantiaEducation}
                      timeout='auto'
                      unmountOnExit
                    >
                      <MenuLink
                        to='/Tanora'
                        icon={Groups}
                        label='Tanórák'
                        level={2}
                      />
                      <MenuLink
                        to='/TanoraWizzard'
                        icon={DashboardCustomize}
                        label='Tanóra varázsló'
                        level={2}
                      />
                      <MenuLink
                        to='/calendar'
                        icon={CalendarMonth}
                        label='Órarend'
                        level={2}
                      />
                      <MenuLink
                        to='/OpenHours'
                        icon={FactCheck}
                        label='Lezáratlan órák'
                        level={2}
                      />
                      <MenuLink
                        to='/createHomework'
                        icon={HomeWork}
                        label='Kiosztott házi feladatok'
                        level={2}
                      />
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaMaster.menu'>
                    <SectionButton
                      title='Törzsadatok'
                      icon={LibraryBooks}
                      open={displayScriptantiaMasterDate}
                      onClick={() =>
                        toggleScriptantiaMasterDate((prev) => !prev)
                      }
                      level={1}
                      color='success'
                    />

                    <Collapse
                      in={displayScriptantiaMasterDate}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Restricted to='scriptantiaMaster.Users'>
                        <MenuLink
                          to='/users'
                          icon={Person}
                          label='Felhasználók - Scriptantia Admin'
                          level={2}
                        />
                      </Restricted>

                      <Restricted to='scriptantiaMaster.Settings'>
                        <MenuLink
                          to='/MasterData'
                          icon={Settings}
                          label='Alapadatok'
                          level={2}
                        />
                        <MenuLink
                          to='/registrations'
                          icon={FactCheck}
                          label='Online regisztrációk'
                          level={2}
                        />
                      </Restricted>

                      <Restricted to='scriptantiaMaster.discounts'>
                        <MenuLink
                          to='/diak-discounts'
                          icon={Percent}
                          label='Diák kedvezmények'
                          level={2}
                        />
                      </Restricted>
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaTasks.menu'>
                    <SectionButton
                      title='Feladatok'
                      icon={Assignment}
                      open={displayScriptantiaTasks}
                      onClick={() => toggleScriptantiaTasks((prev) => !prev)}
                      level={1}
                      color='secondary'
                    />

                    <Collapse
                      in={displayScriptantiaTasks}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Restricted to='tasks.system'>
                        <MenuLink
                          to='/system-tasks'
                          icon={TaskAlt}
                          label='Rendszer feladatok'
                          level={2}
                        />
                      </Restricted>

                      <Restricted to='communication.sms'>
                        <MenuLink
                          to='/sms-list'
                          icon={Sms}
                          label='SMS jóváhagyás'
                          level={2}
                        />
                        <MenuLink
                          to='/sms-create'
                          icon={Sms}
                          label='SMS küldés'
                          level={2}
                        />
                      </Restricted>
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaPayments.menu'>
                    <SectionButton
                      title='Be- és Kifizetések'
                      icon={Paid}
                      open={displayScriptantiaPayments}
                      onClick={() => toggleScriptantiaPayments((prev) => !prev)}
                      level={1}
                      color='success'
                    />

                    <Collapse
                      in={displayScriptantiaPayments}
                      timeout='auto'
                      unmountOnExit
                    >
                      <MenuLink
                        to='/StudentPayements'
                        icon={Money}
                        label='Diák befizetések'
                        level={2}
                      />
                      <MenuLink
                        to='/CouponUseage'
                        icon={Percent}
                        label='Kupon felhasználás'
                        level={2}
                      />
                      <MenuLink
                        to='/CouponSell'
                        icon={PriceChange}
                        label='Kupon értékesítés'
                        level={2}
                      />
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaMovements.menu'>
                    <SectionButton
                      title='Pénzügyek'
                      icon={ReceiptLong}
                      open={displayScriptantiaMovements}
                      onClick={() =>
                        toggleScriptantiaMovements((prev) => !prev)
                      }
                      level={1}
                      color='warning'
                    />

                    <Collapse
                      in={displayScriptantiaMovements}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Restricted to='scriptantiaInvoicing.menu'>
                        <MenuLink
                          to='/invoice-generate'
                          icon={ReceiptLong}
                          label='Számla készítés'
                          level={2}
                        />
                        <MenuLink
                          to='/invoice-manual'
                          icon={ReceiptLong}
                          label='Kézi számla készítés'
                          level={2}
                        />
                        <MenuLink
                          to='/invoice-discounts'
                          icon={Percent}
                          label='Számla kedvezmények'
                          level={2}
                        />
                        <MenuLink
                          to='/prepare-kedvezmeny'
                          icon={Percent}
                          label='Automatikus kedvezmények jóváhagyása'
                          level={2}
                        />
                      </Restricted>

                      <MenuLink
                        to='/StudentPayements'
                        icon={Money}
                        label='Diák befizetések'
                        level={2}
                      />

                      <Restricted to='scriptantiaInvoicing.menu'>
                        <MenuLink
                          to='/Hianyzasok'
                          icon={FactCheck}
                          label='Hiányzások kezelése'
                          level={2}
                        />
                      </Restricted>

                      <MenuLink
                        to='/TeacherPayments'
                        icon={Paid}
                        label='Oktatói kifizetések'
                        level={2}
                      />
                      <MenuLink
                        to='/Costs'
                        icon={Money}
                        label='Költségek'
                        level={2}
                      />
                      <MenuLink
                        to='/Income'
                        icon={PriceChange}
                        label='Bevételek'
                        level={2}
                      />
                      <MenuLink
                        to='/price-list'
                        icon={PriceChange}
                        label='Árlista'
                        level={2}
                      />
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaReports.menu'>
                    <SectionButton
                      title='Kimutatások'
                      icon={BarChart}
                      open={displayScriptantiaReports}
                      onClick={() => toggleScriptantiaReports((prev) => !prev)}
                      level={1}
                      color='info'
                    />

                    <Collapse
                      in={displayScriptantiaReports}
                      timeout='auto'
                      unmountOnExit
                    >
                      <Restricted to='scriptantiaReports.TeacherBalance'>
                        <MenuLink
                          to='/TeacherBalance'
                          icon={BarChart}
                          label='Oktató egyenleg'
                          level={2}
                        />
                      </Restricted>

                      <Restricted to='scriptantiaReports.StudentBalance'>
                        <MenuLink
                          to='/StudentBalance'
                          icon={BarChart}
                          label='Diák egyenleg'
                          level={2}
                        />
                        <MenuLink
                          to='/studentDashboard'
                          icon={BarChart}
                          label='Diák egyenleginfó'
                          level={2}
                        />
                      </Restricted>

                      <Restricted to='scriptantiaReports.AdminReports'>
                        <MenuLink
                          to='/Results'
                          icon={BarChart}
                          label='Eredmények'
                          level={2}
                        />
                        <MenuLink
                          to='/DiakDijak'
                          icon={PriceChange}
                          label='Diák óradíjak áttekintő'
                          level={2}
                        />
                        <MenuLink
                          to='/Latogatottsag'
                          icon={CalendarMonth}
                          label='Óralátogatások'
                          level={2}
                        />
                      </Restricted>
                    </Collapse>
                  </Restricted>

                  <Restricted to='scriptantiaCampaign.menu'>
                    <SectionButton
                      title='E-mail kampányok'
                      icon={Campaign}
                      open={displayScriptantiaCampaigns}
                      onClick={() =>
                        toggleScriptantiaCampaigns((prev) => !prev)
                      }
                      level={1}
                      color='secondary'
                    />

                    <Collapse
                      in={displayScriptantiaCampaigns}
                      timeout='auto'
                      unmountOnExit
                    >
                      <MenuLink
                        to='/MailCampaign'
                        icon={Email}
                        label='Kampányok kezelése'
                        level={2}
                      />
                    </Collapse>
                  </Restricted>
                </Box>
              </Collapse>
            </Restricted>

            <Restricted to='palanta.menu'>
              <SectionButton
                title='Palánta oldal Admin'
                icon={Work}
                open={displayPalanta}
                onClick={() => togglePalanta((prev) => !prev)}
                color='success'
              />

              <Collapse in={displayPalanta} timeout='auto' unmountOnExit>
                <MenuLink
                  to='/palanta-blog'
                  icon={LibraryBooks}
                  label='Blog bejegyzések'
                  level={1}
                />
              </Collapse>
            </Restricted>

            <Restricted to='auth.menu'>
              <SectionButton
                title='Jogosultságok'
                icon={AdminPanelSettings}
                open={displayAuth}
                onClick={() => toggleAuthentication((prev) => !prev)}
                color='primary'
              />

              <Collapse in={displayAuth} timeout='auto' unmountOnExit>
                <MenuLink
                  to='/manage-users'
                  icon={ManageAccounts}
                  label='Felhasználók szerkesztése'
                  level={1}
                />
                <MenuLink
                  to='/user-roles'
                  icon={Groups}
                  label='Szerepkörök'
                  level={1}
                />
                <MenuLink
                  to='/user-rights'
                  icon={VerifiedUser}
                  label='Szerepkör jogosultságok'
                  level={1}
                />
                <MenuLink
                  to='/user-permissions'
                  icon={Key}
                  label='Jogok definiálása'
                  level={1}
                />
                <MenuLink
                  to='/permission-to-role'
                  icon={FactCheck}
                  label='Szerepkör funkciók'
                  level={1}
                />
                <MenuLink
                  to='/apis'
                  icon={Tune}
                  label='API definíciók'
                  level={1}
                />
                <MenuLink
                  to='/api-rights'
                  icon={AdminPanelSettings}
                  label='API jogosultságok'
                  level={1}
                />
                <MenuLink
                  to='/SystemParams'
                  icon={Settings}
                  label='Rendszerparaméterek'
                  level={1}
                />
                <MenuLink
                  to='/SystemSettings'
                  icon={Tune}
                  label='Rendszer beállítások'
                  level={1}
                />
              </Collapse>
            </Restricted>
          </List>
        </CardContent>
      </Card>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  userProfile: state.userProfile,
  params: state.params,
});

export default connect(mapStateToProps, {})(MainMenu);
